import {
  Award,
  BookOpen,
  CalendarX2,
  Clock,
  CornerDownLeft,
  Globe,
  GraduationCap,
  Layers,
  Lightbulb,
  ListChecks,
  Package2,
  Play,
  QrCode,
  ShoppingCart,
  SlidersHorizontal,
  Star,
  Target,
  Truck,
  Zap,
} from 'lucide-react'
import type { ElementType } from 'react'
import { cn } from '@/lib/utils'
import type { RequiredLessonModel } from './derive'

const ICON_MAP: Record<string, ElementType> = {
  ShoppingCart,
  Truck,
  Package2,
  CornerDownLeft,
  SlidersHorizontal,
  CalendarX2,
  ListChecks,
  QrCode,
  BookOpen,
  Lightbulb,
  Target,
  Zap,
  Award,
  Star,
  Layers,
  Globe,
}

const LESSON_ICON_SEQUENCE: (keyof typeof ICON_MAP)[] = [
  'BookOpen',
  'Lightbulb',
  'Target',
  'Zap',
  'Award',
  'Star',
  'Layers',
  'Globe',
]

const ACCENT_BG: Record<string, string> = {
  blue: 'bg-blue-100/80 text-[#2563eb]',
  amber: 'bg-amber-100/80 text-amber-700',
  indigo: 'bg-indigo-100/80 text-indigo-600',
  rose: 'bg-rose-100/80 text-rose-600',
  purple: 'bg-purple-100/80 text-purple-600',
  red: 'bg-red-100/80 text-red-600',
  teal: 'bg-teal-100/80 text-teal-600',
}

const CATEGORY_PILL_BG: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-700',
  amber: 'bg-amber-100 text-amber-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  rose: 'bg-rose-100 text-rose-700',
  purple: 'bg-purple-100 text-purple-700',
  red: 'bg-red-100 text-red-700',
  teal: 'bg-teal-100 text-teal-700',
}

function resolveIconByIndex(lessonIndex: number): ElementType {
  const name = LESSON_ICON_SEQUENCE[lessonIndex % LESSON_ICON_SEQUENCE.length] ?? 'BookOpen'
  return ICON_MAP[name] ?? GraduationCap
}

function resolveAccent(name: string): string {
  return ACCENT_BG[name] ?? 'bg-slate-100 text-slate-600'
}

function resolveCategoryPill(colour: string): string {
  return CATEGORY_PILL_BG[colour] ?? 'bg-slate-100 text-slate-700'
}

/** "Ordering Essentials" → "ORDERING", "Task Management" → "TASK MANAGEMENT" */
export function toCategoryLabel(moduleName: string): string {
  const skip = new Set(['essentials', 'the', 'and', '&', 'a', 'an'])
  const words = moduleName.split(/\s+/).filter((w) => !skip.has(w.toLowerCase()))
  return words.slice(0, 2).join(' ').toUpperCase()
}

export function RequiredLessonCard({
  item,
  onPress,
}: Readonly<{
  item: RequiredLessonModel
  onPress: () => void
}>) {
  const lessonIndex = Math.max(0, Number.parseInt(item.submodule.replace('Lesson ', ''), 10) - 1)
  const Icon = resolveIconByIndex(lessonIndex)
  const category = toCategoryLabel(item.module)
  const pillClass = resolveCategoryPill(item.accentColour)
  const accentClass = resolveAccent(item.accentColour)

  const BUTTON_LABEL: Record<string, string> = {
    complete: 'Review Lesson',
    started: 'Continue',
  }
  const buttonLabel = BUTTON_LABEL[item.state] ?? 'Start Lesson'

  return (
    <button
      type="button"
      onClick={item.comingSoon ? undefined : onPress}
      disabled={item.comingSoon}
      className={cn(
        'w-full text-left px-4 py-4 transition-colors touch-manipulation animate-scroll-reveal',
        item.comingSoon ? 'opacity-60' : 'active:bg-slate-50',
      )}
    >
      <div className="flex gap-3">
        {/* Left: category pill + title + duration */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={cn(
                'px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider',
                pillClass,
              )}
            >
              {category}
            </span>
            {item.comingSoon && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500">
                Coming Soon
              </span>
            )}
            {!item.comingSoon && item.state === 'started' && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">
                In Progress
              </span>
            )}
            {!item.comingSoon && item.state === 'complete' && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">
                Completed
              </span>
            )}
          </div>
          <h3 className="text-base font-bold text-[#131b2e] leading-snug">{item.title}</h3>
          <p className="text-sm text-slate-600 leading-snug">
            {item.submodule}: {item.module}
          </p>
          <div className="flex items-center gap-1 text-sm text-slate-600">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            <span>{item.estimatedMinutes} mins</span>
          </div>
        </div>
        {/* Right: thumbnail */}
        <div
          className={cn(
            'w-20 h-20 rounded-xl flex items-center justify-center shrink-0',
            accentClass,
          )}
        >
          <Icon className="w-8 h-8 opacity-80" />
        </div>
      </div>
      {/* Action button */}
      <div className="mt-3 flex justify-end">
        {item.comingSoon ? (
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-100 text-slate-400 text-xs font-bold">
            Coming Soon
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#2563eb] text-white text-xs font-bold">
            <Play className="w-3 h-3 fill-white" />
            {buttonLabel}
          </span>
        )}
      </div>
    </button>
  )
}
